import React from 'react';
import StatusArrowRight from '../../../../assets/svg/status-arrow-right.svg';
import { VIEW_SERVICE_REQUEST_LABELS as L } from '../../constants/labels';
import type {
  ServiceRequestHistoryEntry,
  ServiceRequestRole,
  ViewServiceRequestDetail,
} from '../../types/viewServiceRequestType';
import { formatRole, formatSubmittedOn } from './viewServiceRequestHelpers';
import { TimelineAvatarCircle, TimelineConnector } from './SRHistoryTimeline';
import { EmptyTabState, SRStatusBadge } from './viewServiceRequestUI';

interface EventLabelRowProps {
  entry: ServiceRequestHistoryEntry;
}

const EventLabelRow: React.FC<EventLabelRowProps> = ({ entry }) => {
  const labelClass =
    'text-sm font-normal leading-5 tracking-[0.25px] text-[#181A1B] wrap-break-word';

  // Derive event type from data: previous_status===null always means creation,
  // regardless of the action_type value stored in the DB (old rows were backfilled
  // with the wrong default 'status_changed').
  if (entry.previous_status === null) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className={labelClass}>{L.HISTORY_SERVICE_REQUEST_CREATED}</span>
        <SRStatusBadge status={entry.new_status} />
      </div>
    );
  }

  if (
    entry.action_type === 'technician_assigned' ||
    entry.new_status === 'assigned'
  ) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className={labelClass}>{L.HISTORY_TECHNICIAN_ASSIGNED}</span>
        {entry.previous_status && (
          <SRStatusBadge status={entry.previous_status} />
        )}
        {entry.previous_status && (
          <StatusArrowRight className="shrink-0" width={16} height={16} />
        )}
        <SRStatusBadge status={entry.new_status} />
      </div>
    );
  }

  if (entry.action_type === 'technician_accepted') {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className={labelClass}>{L.HISTORY_TECHNICIAN_ACCEPTED}</span>
        {entry.previous_status && (
          <SRStatusBadge status={entry.previous_status} />
        )}
        {entry.previous_status && (
          <StatusArrowRight className="shrink-0" width={16} height={16} />
        )}
        <SRStatusBadge status={entry.new_status} />
      </div>
    );
  }

  if (entry.action_type === 'technician_rejected') {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className={labelClass}>{L.HISTORY_TECHNICIAN_REJECTED}</span>
        {entry.previous_status && (
          <SRStatusBadge status={entry.previous_status} />
        )}
        {entry.previous_status && (
          <StatusArrowRight className="shrink-0" width={16} height={16} />
        )}
        <SRStatusBadge status={entry.new_status} />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className={labelClass}>{L.HISTORY_STATUS_CHANGE}</span>
      <SRStatusBadge status={entry.previous_status} />
      <StatusArrowRight className="shrink-0" width={16} height={16} />
      <SRStatusBadge status={entry.new_status} />
    </div>
  );
};

interface SRHistoryEntryRowProps {
  entry: ServiceRequestHistoryEntry;
  isLast: boolean;
  testId: string;
}

const SRHistoryEntryRow: React.FC<SRHistoryEntryRowProps> = ({
  entry,
  isLast,
  testId,
}) => {
  const actor = entry.created_by;
  const name = actor?.name ?? '';
  const imageUrl = actor?.profile?.url?.trim() || undefined;

  return (
    <div className="flex gap-3" data-testid={testId}>
      {/* Avatar circle + connector */}
      <div className="flex flex-col items-center">
        <TimelineAvatarCircle name={name} imageUrl={imageUrl} />
        {!isLast && <TimelineConnector testId={`${testId}-connector`} />}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1 min-w-0 pb-8">
        <EventLabelRow entry={entry} />
        {actor && (
          <p className="text-sm font-normal leading-5 tracking-[0.25px] text-[#474A4C] w-full wrap-break-word">
            {entry.previous_status === null
              ? L.HISTORY_CREATED_BY
              : L.HISTORY_UPDATED_BY}
            : {actor.name}
            {actor.role
              ? ` (${formatRole(actor.role as ServiceRequestRole)})`
              : ''}
          </p>
        )}
        <p
          className="text-xs font-normal leading-4 tracking-[0.40px] text-[#474A4C]"
          data-testid={`${testId}-date`}
        >
          {formatSubmittedOn(entry.created_at)}
        </p>
      </div>
    </div>
  );
};

interface SRHistoryTabProps {
  detail: ViewServiceRequestDetail | undefined;
}

const SRHistoryTab: React.FC<SRHistoryTabProps> = ({ detail }) => {
  const history = detail?.history ?? [];

  return (
    <div
      className="border border-[#DCDDE0] rounded-lg flex flex-col flex-1 min-h-0 overflow-y-auto"
      data-testid="view-sr-history-card"
    >
      <p className="text-lg font-medium leading-6.5 text-[#474A4C] sticky top-0 bg-white px-5 pt-5 pb-4 rounded-t-lg z-10">
        {L.NAV_HISTORY}
      </p>
      {history.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyTabState
            title={L.HISTORY_EMPTY_TITLE}
            description={L.HISTORY_EMPTY_DESCRIPTION}
            testId="view-sr-history-empty"
          />
        </div>
      ) : (
        <div className="flex flex-col px-5 pb-5">
          {history.map((entry, idx) => (
            <SRHistoryEntryRow
              key={`${entry.action_type}-${entry.created_at}-${idx}`}
              entry={entry}
              isLast={idx === history.length - 1}
              testId={`view-sr-history-entry-${idx}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SRHistoryTab;
